import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import { Plus, LogOut, Clock, Users, UserCircle, Search, Lightbulb, ClipboardList, Zap, Globe, Layers, TrendingUp, Calendar, Flame, Trash2 } from 'lucide-react';
import { trackPageView } from '@/utils/analytics';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [filterTypes, setFilterTypes] = useState({
    idea_pool: true,
    speed_run_quiz: true,
    questionnaire: true
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Detect mobile - portrait orientation (height > width)
    const checkMobile = () => {
      setIsMobile(window.innerHeight > window.innerWidth);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?signup=true');
      return;
    }
    
    trackPageView('dashboard');
    fetchSessions();
  }, [navigate]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
    } catch (error) {
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const handleSessionClick = (session) => {
    // Store referrer for back navigation
    sessionStorage.setItem('referrer', '/dashboard');
    
    if (session.status === 'draft') {
      // Navigate to edit/setup page
      if (session.session_type === 'questionnaire') {
        navigate(`/session-setup/questionnaire?draft=${session.id}`);
      } else if (session.session_type === 'idea_pool') {
        navigate(`/session-setup/idea-pool?draft=${session.id}`);
      } else if (session.session_type === 'speed_run_quiz') {
        navigate(`/session-setup/speed-run-quiz?draft=${session.id}`);
      }
    } else {
      // Navigate to summary/results based on session type
      if (session.session_type === 'speed_run_quiz') {
        navigate(`/speed-run/${session.id}/summary`);
      } else if (session.session_type === 'idea_pool') {
        navigate(`/session/${session.id}/facilitator`);
      } else if (session.session_type === 'questionnaire') {
        navigate(`/facilitator/${session.id}`);
      }
    }
  };

  const handleDeleteClick = (e, session) => {
    e.stopPropagation();
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!sessionToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/sessions/${sessionToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session deleted successfully');
      fetchSessions();
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const getCountryFlag = (countryCode) => {
    if (!countryCode || countryCode.length !== 2) {
      return '🌐';
    }
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const getSessionTypeIcon = (type) => {
    switch (type) {
      case 'idea_pool':
        return <Lightbulb className="h-4 w-4" />;
      case 'questionnaire':
        return <ClipboardList className="h-4 w-4" />;
      case 'speed_run_quiz':
        return <Zap className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getSessionTypeColor = (type) => {
    switch (type) {
      case 'idea_pool':
        return 'from-purple-500 to-pink-500';
      case 'questionnaire':
        return 'from-blue-500 to-cyan-500';
      case 'speed_run_quiz':
        return 'from-orange-500 to-red-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getSessionTypeName = (type) => {
    switch (type) {
      case 'idea_pool':
        return 'Idea Pool';
      case 'questionnaire':
        return 'Questionnaire';
      case 'speed_run_quiz':
        return 'Speed Quiz';
      default:
        return type;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'completed':
        return <Badge variant="outline">Completed</Badge>;
      default:
        return null;
    }
  };

  const filteredSessions = sessions.filter(session => {
    const matchesFilter = filterTypes[session.session_type];
    const matchesSearch = !searchQuery || 
      session.topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      session.hashtags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) ||
      session.country_code?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesTab = true;
    if (activeTab === 'active') {
      matchesTab = session.status === 'active';
    } else if (activeTab === 'completed') {
      matchesTab = session.status === 'completed';
    } else if (activeTab === 'drafts') {
      matchesTab = session.status === 'draft';
    }
    
    return matchesFilter && matchesSearch && matchesTab;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {!isMobile ? (
        <>
          {/* Desktop Layout */}
          {/* Top Header */}
          <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img alt="Pollar Logo" className="w-6 h-6" src="/assets/logo/pollar.svg" />
              <h1 className="text-2xl font-bold text-purple-600">Pollar</h1>
            </div>

            {/* Search Bar */}
            <div className="flex-1 max-w-2xl mx-8">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search your sessions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full bg-gray-100 border-0"
                />
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/session/setup')}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Session
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full w-10 h-10 p-0">
                    <UserCircle className="h-6 w-6" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <UserCircle className="h-4 w-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Left Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div className="sticky top-24">
              {/* Menu */}
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Menu</h3>
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => navigate('/social')}
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    Explore
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-purple-600 bg-purple-50"
                    onClick={() => navigate('/dashboard')}
                  >
                    <Layers className="h-4 w-4 mr-2" />
                    My Sessions
                  </Button>
                </div>
              </div>

              {/* Filter by Type */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Filter by Type</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Checkbox
                      id="idea_pool"
                      checked={filterTypes.idea_pool}
                      onCheckedChange={(checked) => setFilterTypes({ ...filterTypes, idea_pool: checked })}
                    />
                    <label htmlFor="idea_pool" className="ml-2 text-sm cursor-pointer flex items-center">
                      <Lightbulb className="h-4 w-4 mr-2 text-purple-600" />
                      Idea Pool
                    </label>
                  </div>
                  <div className="flex items-center">
                    <Checkbox
                      id="speed_quiz"
                      checked={filterTypes.speed_run_quiz}
                      onCheckedChange={(checked) => setFilterTypes({ ...filterTypes, speed_run_quiz: checked })}
                    />
                    <label htmlFor="speed_quiz" className="ml-2 text-sm cursor-pointer flex items-center">
                      <Zap className="h-4 w-4 mr-2 text-orange-600" />
                      Speed Quiz
                    </label>
                  </div>
                  <div className="flex items-center">
                    <Checkbox
                      id="questionnaire"
                      checked={filterTypes.questionnaire}
                      onCheckedChange={(checked) => setFilterTypes({ ...filterTypes, questionnaire: checked })}
                    />
                    <label htmlFor="questionnaire" className="ml-2 text-sm cursor-pointer flex items-center">
                      <ClipboardList className="h-4 w-4 mr-2 text-blue-600" />
                      Questionnaire
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {/* Tabs */}
            <div className="flex gap-6 mb-6 border-b">
              <button
                onClick={() => setActiveTab('all')}
                className={`pb-3 px-1 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === 'all'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Layers className="h-4 w-4" />
                All Sessions
              </button>
              <button
                onClick={() => setActiveTab('active')}
                className={`pb-3 px-1 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === 'active'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Flame className="h-4 w-4" />
                Active
              </button>
              <button
                onClick={() => setActiveTab('completed')}
                className={`pb-3 px-1 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === 'completed'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <TrendingUp className="h-4 w-4" />
                Completed
              </button>
              <button
                onClick={() => setActiveTab('drafts')}
                className={`pb-3 px-1 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === 'drafts'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Calendar className="h-4 w-4" />
                Drafts
              </button>
            </div>

            {/* Sessions Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading your sessions...</p>
              </div>
            ) : filteredSessions.length === 0 ? (
              <div className="text-center py-12">
                <Layers className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
                <p className="text-gray-600 mb-4">Create your first session to get started</p>
                <Button
                  onClick={() => navigate('/session/setup')}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Session
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredSessions.map((session) => (
                  <Card
                    key={session.id}
                    className="cursor-pointer hover:shadow-lg transition-shadow overflow-hidden relative group"
                    onClick={() => handleSessionClick(session)}
                  >
                    {/* Colored Top Border */}
                    <div className={`h-2 bg-gradient-to-r ${getSessionTypeColor(session.session_type)}`} />
                    
                    {/* Delete Button */}
                    <button
                      onClick={(e) => handleDeleteClick(e, session)}
                      className="absolute top-4 right-4 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10 hover:bg-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>

                    <CardContent className="p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className={`p-2 rounded-lg bg-gradient-to-r ${getSessionTypeColor(session.session_type)} text-white`}>
                            {getSessionTypeIcon(session.session_type)}
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {getSessionTypeName(session.session_type)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(session.status)}
                          <div className="text-2xl">
                            {getCountryFlag(session.country_code)}
                          </div>
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                        {session.topic}
                      </h3>

                      {/* Hashtags */}
                      {session.hashtags && session.hashtags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {session.hashtags.slice(0, 3).map((tag, index) => (
                            <span key={index} className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            <span>{new Date(session.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            <span>{session.participant_count || 0} joined</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
        </>
      ) : (
        <>
          {/* Mobile Layout */}
          {/* Mobile Top Header */}
          <div className="bg-white border-b sticky top-0 z-50">
            <div className="flex items-center justify-between px-4 h-14">
              <div className="flex items-center gap-2">
                <img alt="Pollar Logo" className="w-6 h-6" src="/assets/logo/pollar.svg" />
                <h1 className="text-xl font-bold text-purple-600">Pollar</h1>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowMobileSearch(!showMobileSearch)}>
                  <Search className="h-5 w-5 text-gray-600" />
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button>
                      <UserCircle className="h-6 w-6 text-gray-600" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      <UserCircle className="h-4 w-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Mobile Search Bar */}
            {showMobileSearch && (
              <div className="px-4 pb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full bg-gray-100 border-0"
                  />
                </div>
              </div>
            )}

            {/* Mobile Tabs */}
            <div className="overflow-x-auto scrollbar-hide border-b">
              <div className="flex gap-2 px-4 py-2 min-w-max">
                <Button
                  size="sm"
                  variant={activeTab === 'all' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('all')}
                  className={activeTab === 'all' ? 'bg-purple-600' : ''}
                >
                  <Layers className="h-3 w-3 mr-1" />
                  All
                </Button>
                <Button
                  size="sm"
                  variant={activeTab === 'active' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('active')}
                  className={activeTab === 'active' ? 'bg-green-600' : ''}
                >
                  <Flame className="h-3 w-3 mr-1" />
                  Active
                </Button>
                <Button
                  size="sm"
                  variant={activeTab === 'completed' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('completed')}
                  className={activeTab === 'completed' ? 'bg-gray-600' : ''}
                >
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Done
                </Button>
                <Button
                  size="sm"
                  variant={activeTab === 'drafts' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('drafts')}
                  className={activeTab === 'drafts' ? 'bg-gray-500' : ''}
                >
                  <Calendar className="h-3 w-3 mr-1" />
                  Drafts
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile Content */}
          <div className="pb-20 px-4 pt-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading...</p>
              </div>
            ) : filteredSessions.length === 0 ? (
              <div className="text-center py-12">
                <Layers className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions</h3>
                <p className="text-gray-600 mb-4">Create your first session</p>
                <Button
                  onClick={() => navigate('/session/setup')}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Session
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredSessions.map((session) => (
                  <Card
                    key={session.id}
                    className="cursor-pointer active:scale-98 transition-transform overflow-hidden relative"
                    onClick={() => handleSessionClick(session)}
                  >
                    <div className={`h-1 bg-gradient-to-r ${getSessionTypeColor(session.session_type)}`} />
                    
                    <button
                      onClick={(e) => handleDeleteClick(e, session)}
                      className="absolute top-3 right-3 p-1.5 bg-red-500 text-white rounded-full z-10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>

                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg bg-gradient-to-r ${getSessionTypeColor(session.session_type)} text-white`}>
                            {getSessionTypeIcon(session.session_type)}
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {getSessionTypeName(session.session_type)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(session.status)}
                          <div className="text-xl">
                            {getCountryFlag(session.country_code)}
                          </div>
                        </div>
                      </div>

                      <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2">
                        {session.topic}
                      </h3>

                      {session.hashtags && session.hashtags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {session.hashtags.slice(0, 2).map((tag, index) => (
                            <span key={index} className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            <span>{session.participant_count || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{new Date(session.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Bottom Navigation */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t z-50">
            <div className="flex items-center justify-around py-2">
              <button 
                onClick={() => navigate('/dashboard')}
                className="flex flex-col items-center p-2 text-purple-600"
              >
                <Layers className="h-5 w-5" />
                <span className="text-xs mt-1">My Sessions</span>
              </button>
              <button 
                onClick={() => navigate('/session/setup')}
                className="flex flex-col items-center -mt-6"
              >
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full p-4 shadow-lg">
                  <Plus className="h-6 w-6 text-white" />
                </div>
              </button>
              <button 
                onClick={() => navigate('/social')}
                className="flex flex-col items-center p-2 text-gray-500"
              >
                <Globe className="h-5 w-5" />
                <span className="text-xs mt-1">Explore</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Session</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{sessionToDelete?.topic}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
